import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, Home, RefreshCw, Search, Plus, Pencil, Trash2, X, Eye, EyeOff, Save, Loader2, Copy,
  FileDown, FileUp, FileSpreadsheet, SlidersHorizontal, Filter, RotateCcw, ChevronLeft, ChevronRight,
  FileSearch,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import { SEGMENTS, TABLE_COLUMNS, ALL_FIELDS, initialForType, JK_OPTIONS } from "@/lib/santriSchema";
import AdminNav from "@/components/AdminNav";
import SantriPreviewModal from "@/components/SantriPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default visible columns (compact view — user can toggle rest)
const DEFAULT_VISIBLE = new Set([
  "no", "username", "password", "nama_santri", "jenis_kelamin", "status_santri",
  "nama_pembimbing", "jumlah_hafalan_terakhir",
  "daerah_asal", "no_kontak_santri", "nama_ayah", "nama_ibu",
]);

const EMPTY_RECORD = () => {
  const rec = { username: "", password: "" };
  ALL_FIELDS.forEach((f) => {
    if (rec[f.key] !== undefined) return;
    if (f.type === "computed") return;
    rec[f.key] = initialForType(f.type);
  });
  return rec;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [jkFilter, setJkFilter] = useState("all");
  const [visibleCols, setVisibleCols] = useState(() =>
    Object.fromEntries(TABLE_COLUMNS.map((c) => [c.key, DEFAULT_VISIBLE.has(c.key)]))
  );
  const [showColsMenu, setShowColsMenu] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [modal, setModal] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [ioBusy, setIoBusy] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/santri`, { headers: authHeader() });
      setRows(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/login", { replace: true });
      } else {
        toast.error("Gagal memuat data santri");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (jkFilter !== "all" && (r.jenis_kelamin || "") !== jkFilter) return false;
      if (!s) return true;
      return Object.values(r).some((v) => String(v || "").toLowerCase().includes(s));
    });
  }, [rows, q, jkFilter]);

  const counts = useMemo(() => {
    const c = { total: rows.length, Ikhwan: 0, Akhwat: 0 };
    rows.forEach((r) => {
      if (r.jenis_kelamin === "Ikhwan") c.Ikhwan += 1;
      else if (r.jenis_kelamin === "Akhwat") c.Akhwat += 1;
    });
    return c;
  }, [rows]);

  const toggleCol = (k) => setVisibleCols((v) => ({ ...v, [k]: !v[k] }));
  const showAllCols = () => setVisibleCols(Object.fromEntries(TABLE_COLUMNS.map((c) => [c.key, true])));
  const resetCols = () => setVisibleCols(Object.fromEntries(TABLE_COLUMNS.map((c) => [c.key, DEFAULT_VISIBLE.has(c.key)])));
  const activeCols = TABLE_COLUMNS.filter((c) => visibleCols[c.key]);

  const doLogout = () => { logout(); navigate("/", { replace: true }); };

  const openCreate = () => setModal({ mode: "create", data: EMPTY_RECORD() });
  const openEdit = (r) => {
    const filled = { ...EMPTY_RECORD(), ...r };
    setModal({ mode: "edit", data: filled });
  };

  const saveModal = async () => {
    const d = modal.data;
    setBusyId("modal");
    try {
      if (modal.mode === "create") {
        if (!d.username || !d.password) {
          toast.error("Username dan password wajib diisi");
          setBusyId(null);
          return;
        }
        const res = await axios.post(`${API}/santri`, d, { headers: authHeader() });
        toast.success(`Santri ${res.data.username} ditambahkan`);
      } else {
        const patch = { ...d };
        delete patch.tahun_masuk_grq; // derived
        if (!patch.password) delete patch.password;
        const res = await axios.patch(`${API}/santri/${d.id || d.username}`, patch, { headers: authHeader() });
        toast.success(`Data ${res.data.username} diperbarui`);
      }
      setModal(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan data";
      toast.error(typeof msg === "string" ? msg : "Gagal menyimpan");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    const r = confirmDel;
    setBusyId(r.id);
    try {
      await axios.delete(`${API}/santri/${r.id || r.username}`, { headers: authHeader() });
      toast.success(`Data ${r.username} dihapus`);
      setConfirmDel(null);
      await load();
    } catch { toast.error("Gagal menghapus"); }
    finally { setBusyId(null); }
  };

  const copyCred = (r) => {
    navigator.clipboard?.writeText(`${r.username} / ${r.password}`);
    toast.success("Kredensial disalin");
  };

  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const doDownloadTemplate = async () => {
    setIoBusy("template");
    try {
      const res = await axios.get(`${API}/santri/template.xlsx`, { headers: authHeader(), responseType: "blob" });
      downloadBlob(res.data, "blanko_santri_grq.xlsx");
      toast.success("Blanko diunduh");
    } catch { toast.error("Gagal mengunduh blanko"); }
    finally { setIoBusy(null); }
  };

  const doExport = async () => {
    setIoBusy("export");
    try {
      const res = await axios.get(`${API}/santri/export.xlsx`, { headers: authHeader(), responseType: "blob" });
      const ts = new Date().toISOString().slice(0, 10);
      downloadBlob(res.data, `data_santri_grq_${ts}.xlsx`);
      toast.success("Data santri diekspor");
    } catch { toast.error("Gagal mengekspor data"); }
    finally { setIoBusy(null); }
  };

  const onPickImport = () => fileInputRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      toast.error("Format harus .xlsx, .xls, atau .csv"); return;
    }
    setIoBusy("import");
    try {
      const form = new FormData(); form.append("file", file);
      const res = await axios.post(`${API}/santri/import`, form, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      const { created, updated, skipped } = res.data;
      toast.success(`Import selesai: +${created} baru, ${updated} diperbarui, ${skipped} dilewati`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gagal mengimpor file";
      toast.error(typeof msg === "string" ? msg : "Gagal mengimpor");
    } finally { setIoBusy(null); }
  };

  return (
    <main className="min-h-screen bg-paper text-forest">
      <AdminNav user={user} active="santri" />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-xs tracking-[0.4em] uppercase text-gold">Dashboard Admin</span>
          <h1 className="mt-3 font-serif-display text-forest text-4xl md:text-5xl leading-tight">
            Data Santri,<br /><span className="italic text-gold">satu tempat.</span>
          </h1>
          <p className="mt-4 text-moss max-w-2xl text-sm">
            Kelola akun & profil santri di sini. Perubahan pada <b>Username</b> dan <b>Password</b> otomatis berpengaruh ke akses login santri.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-col gap-4">
          {/* Top actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari nama / username / kontak / dsb"
                  className="pl-9 pr-4 py-2.5 bg-white/70 border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 min-w-[300px]"
                  data-testid="admin-search"
                />
              </div>
              <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-forest text-sm hover:bg-gold hover:text-forest transition-colors" data-testid="admin-refresh">
                <RefreshCw size={14} /> Refresh
              </button>
              <button onClick={() => setShowPass((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-forest text-sm hover:bg-gold hover:text-forest transition-colors" data-testid="admin-toggle-pw">
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                {showPass ? "Sembunyikan Password" : "Tampilkan Password"}
              </button>
            </div>
            <button onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors" data-testid="admin-add-santri">
              <Plus size={16} /> Tambah Santri
            </button>
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-3 p-4 rounded-2xl border border-gold/30 bg-white/50">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold flex items-center gap-2"><Filter size={14} /> Filter</span>
            <div className="inline-flex items-center rounded-full bg-sand p-1 border border-gold/20" role="group">
              {[
                { k: "all", label: `Semua (${counts.total})` },
                { k: "Ikhwan", label: `Ikhwan (${counts.Ikhwan})` },
                { k: "Akhwat", label: `Akhwat (${counts.Akhwat})` },
              ].map((opt) => (
                <button key={opt.k} onClick={() => setJkFilter(opt.k)}
                  className={`px-3.5 py-1.5 rounded-full text-xs transition-colors ${jkFilter === opt.k ? "bg-forest text-parchment" : "text-forest hover:bg-white"}`}
                  data-testid={`filter-jk-${opt.k}`}>{opt.label}</button>
              ))}
            </div>
            <div className="relative">
              <button onClick={() => setShowColsMenu((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold hover:text-forest transition-colors" data-testid="admin-columns-toggle">
                <SlidersHorizontal size={14} /> Kolom ({activeCols.length}/{TABLE_COLUMNS.length})
              </button>
              {showColsMenu && (
                <div className="absolute z-40 top-full left-0 mt-2 w-80 p-4 rounded-2xl border border-gold/40 bg-parchment shadow-xl" data-testid="admin-columns-menu">
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Tampilkan Kolom</span>
                    <div className="flex items-center gap-2">
                      <button onClick={showAllCols} className="text-[10px] uppercase tracking-widest text-moss hover:text-gold">Semua</button>
                      <button onClick={resetCols} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-moss hover:text-gold" data-testid="admin-columns-reset">
                        <RotateCcw size={11} /> Reset
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-1 max-h-96 overflow-y-auto">
                    {TABLE_COLUMNS.map((c) => (
                      <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-sand cursor-pointer text-xs text-forest">
                        <input type="checkbox" checked={!!visibleCols[c.key]} onChange={() => toggleCol(c.key)} disabled={c.key === "no"} className="accent-forest" data-testid={`col-toggle-${c.key}`} />
                        <span className={c.key === "no" ? "text-moss" : ""}>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {(jkFilter !== "all" || q) && (
              <button onClick={() => { setJkFilter("all"); setQ(""); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-moss hover:text-gold" data-testid="admin-clear-filters">
                <X size={12} /> Bersihkan filter
              </button>
            )}
            <span className="ml-auto text-[11px] text-moss">
              Menampilkan <b>{filtered.length}</b> dari {rows.length} santri
            </span>
          </div>

          {/* Excel toolbar */}
          <div className="flex flex-wrap items-center gap-3 p-4 rounded-2xl border border-gold/30 bg-sand/60">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold flex items-center gap-2"><FileSpreadsheet size={14} /> Excel / CSV</span>
            <button onClick={doDownloadTemplate} disabled={ioBusy === "template"} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gold/40 text-forest text-xs hover:bg-gold hover:text-forest transition-colors disabled:opacity-60" data-testid="admin-download-template">
              {ioBusy === "template" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Unduh Blanko
            </button>
            <button onClick={doExport} disabled={ioBusy === "export"} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gold/40 text-forest text-xs hover:bg-gold hover:text-forest transition-colors disabled:opacity-60" data-testid="admin-export">
              {ioBusy === "export" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Export Data Santri
            </button>
            <button onClick={onPickImport} disabled={ioBusy === "import"} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors disabled:opacity-60" data-testid="admin-import">
              {ioBusy === "import" ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Import Massal
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onImportFile} className="hidden" data-testid="admin-import-input" />
            <span className="text-[11px] text-moss ml-auto">
              Format: .xlsx / .xls / .csv — Username &amp; Password wajib. Baris dengan Username yang sudah ada akan diperbarui.
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-forest text-parchment text-left">
                <tr>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Aksi</th>
                  {activeCols.map((c) => (
                    <th key={c.key} className="px-3 py-3 font-medium whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={activeCols.length + 1} className="text-center py-10 text-moss">Memuat data…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={activeCols.length + 1} className="text-center py-10 text-moss">Tidak ada data yang cocok dengan filter.</td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={r.id || r.username} className="border-t border-gold/15 hover:bg-sand/60 transition-colors" data-testid={`santri-row-${r.username}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setPreviewRow(r)} className="p-2 rounded-full hover:bg-forest hover:text-parchment transition-colors" aria-label="Preview data lengkap" title="Preview data lengkap" data-testid={`preview-${r.username}`}>
                            <FileSearch size={14} />
                          </button>
                          <button onClick={() => openEdit(r)} className="p-2 rounded-full hover:bg-gold hover:text-forest transition-colors" aria-label="Edit" data-testid={`edit-${r.username}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => setConfirmDel(r)} className="p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors" aria-label="Hapus" data-testid={`del-${r.username}`} disabled={busyId === r.id}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                      {activeCols.map((c) => (
                        <td key={c.key} className={`px-3 py-2.5 ${c.key === "alamat" || c.key.startsWith("alasan_") ? "max-w-[260px] truncate" : "whitespace-nowrap"} ${c.key === "username" ? "font-medium" : ""} ${c.key === "no" ? "text-moss" : ""}`} title={c.key === "alamat" ? r.alamat : undefined}>
                          {c.key === "no" ? i + 1 :
                           c.key === "password" ? (
                             <span className="inline-flex items-center gap-1">
                               <code className="text-forest">{showPass ? r.password || "—" : "••••••••"}</code>
                               <button onClick={() => copyCred(r)} className="text-moss hover:text-gold p-0.5" aria-label="Salin" data-testid={`copy-cred-${r.username}`}>
                                 <Copy size={12} />
                               </button>
                             </span>
                           ) : r[c.key] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-3 text-xs text-moss">
          Total: <b>{filtered.length}</b> santri {q && `(difilter dari ${rows.length})`}
        </p>
      </section>

      <AnimatePresence>
        {modal && (
          <SantriModal
            state={modal}
            onChange={(next) => setModal({ ...modal, data: next })}
            onClose={() => setModal(null)}
            onSave={saveModal}
            saving={busyId === "modal"}
          />
        )}
        {confirmDel && (
          <ConfirmDialog record={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={confirmDelete} busy={busyId === confirmDel.id} />
        )}
        {importResult && (
          <ImportResultDialog result={importResult} onClose={() => setImportResult(null)} />
        )}
        {previewRow && (
          <SantriPreviewModal
            record={previewRow}
            onClose={() => setPreviewRow(null)}
            includeCredentials={true}
          />
        )}
      </AnimatePresence>
    </main>
  );
}

function DashboardNav_UNUSED({ user, onLogout }) {
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-parchment/85 backdrop-blur-md border-b border-gold/30">
      <nav className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="dash-brand">
          <img src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png" alt="Logo GRQ" className="h-11 w-11 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-moss">Portal Admin</div>
            <div className="font-serif-display text-forest text-base">Generasi Rabbani Qurani</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-xs text-moss">
            <span className="tracking-[0.2em] uppercase">Masuk sebagai</span>{" "}
            <span className="text-forest font-medium">{user?.name}</span>
          </span>
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold hover:text-forest transition-colors" data-testid="dash-home">
            <Home size={14} /> Beranda
          </Link>
          <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors" data-testid="dash-logout">
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </nav>
    </header>
  );
}

// ----------------- SantriModal (segmented) -----------------
function SantriModal({ state, onChange, onClose, onSave, saving }) {
  const isCreate = state.mode === "create";
  const d = state.data;
  const [activeSeg, setActiveSeg] = useState(SEGMENTS[0].id);
  const [pembimbingNames, setPembimbingNames] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/pembimbing/names`, { headers: authHeader() })
      .then((r) => setPembimbingNames(r.data || []))
      .catch(() => setPembimbingNames([]));
  }, []);

  const set = (k, v) => onChange({ ...d, [k]: v });

  // auto-derive tahun_masuk_grq
  const derivedTahun = useMemo(() => {
    const m = String(d.tanggal_masuk_grq || "").match(/(\d{4})/);
    return m ? m[1] : "";
  }, [d.tanggal_masuk_grq]);

  const seg = SEGMENTS.find((s) => s.id === activeSeg);
  const segIndex = SEGMENTS.findIndex((s) => s.id === activeSeg);
  const goPrev = () => segIndex > 0 && setActiveSeg(SEGMENTS[segIndex - 1].id);
  const goNext = () => segIndex < SEGMENTS.length - 1 && setActiveSeg(SEGMENTS[segIndex + 1].id);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
      onClick={onClose} data-testid="santri-modal">
      <motion.div
        initial={{ y: 30, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-parchment rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden border border-gold/40 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold">{isCreate ? "Tambah Santri" : "Edit Santri"}</span>
            <h3 className="font-serif-display text-forest text-2xl">{isCreate ? "Data santri baru" : d.nama_santri || d.username}</h3>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2" aria-label="Tutup"><X size={18} /></button>
        </div>

        {/* Segment tabs */}
        <div className="px-4 md:px-6 pt-4 border-b border-gold/20">
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-3">
            {SEGMENTS.map((s) => (
              <button key={s.id} onClick={() => setActiveSeg(s.id)}
                className={`px-3.5 py-2 rounded-full text-xs whitespace-nowrap transition-colors ${activeSeg === s.id ? "bg-forest text-parchment" : "bg-sand text-forest hover:bg-white"}`}
                data-testid={`seg-tab-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fields */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {seg.fields.map((f) => (
              <FieldEditor
                key={f.key}
                field={f}
                value={f.key === "tahun_masuk_grq" ? derivedTahun : d[f.key]}
                onChange={(v) => set(f.key, v)}
                allData={d}
                readOnly={f.type === "computed"}
                isCreate={isCreate}
                pembimbingNames={pembimbingNames}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gold/20 bg-parchment/95">
          <div className="flex items-center gap-2">
            <button onClick={goPrev} disabled={segIndex === 0} className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs text-forest hover:bg-sand disabled:opacity-40 disabled:cursor-not-allowed" data-testid="seg-prev">
              <ChevronLeft size={14} /> Sebelumnya
            </button>
            <button onClick={goNext} disabled={segIndex === SEGMENTS.length - 1} className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs text-forest hover:bg-sand disabled:opacity-40 disabled:cursor-not-allowed" data-testid="seg-next">
              Berikutnya <ChevronRight size={14} />
            </button>
            <span className="text-[10px] text-moss ml-3">Segmen {segIndex + 1}/{SEGMENTS.length}</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-5 py-2.5 rounded-full text-forest text-sm hover:bg-sand transition-colors" data-testid="modal-cancel">Batal</button>
            <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors disabled:opacity-60" data-testid="modal-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function FieldEditor({ field, value, onChange, allData, readOnly, isCreate, pembimbingNames }) {
  const isWide = field.type === "textarea";
  const showQuick = Array.isArray(field.quickFill) && field.quickFill.length > 0;

  const handleQuick = (qf) => {
    if (qf.copyFrom && allData?.[qf.copyFrom]) {
      onChange(allData[qf.copyFrom]);
    } else if (qf.literal) {
      onChange(qf.literal);
    } else if (qf.copyFrom) {
      onChange("");
    }
  };

  const passwordHelp = isCreate ? "" : (field.key === "password" ? "Kosongkan jika tidak diubah" : "");

  return (
    <div className={isWide ? "md:col-span-2" : ""}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="block text-[10px] tracking-[0.2em] uppercase text-moss">
          {field.label}{field.required ? " *" : ""}
        </label>
        {showQuick && (
          <div className="flex gap-1">
            {field.quickFill.map((qf, i) => (
              <button key={i} type="button" onClick={() => handleQuick(qf)}
                className="text-[9px] tracking-widest uppercase text-gold hover:text-forest border border-gold/30 rounded-full px-2 py-0.5"
                data-testid={`quick-${field.key}-${i}`}>
                {qf.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {field.type === "image" ? (
        <ImageUploader value={value} onChange={onChange} testId={`modal-${field.key}`} />
      ) : field.type === "select" ? (
        <select
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm disabled:opacity-70"
          data-testid={`modal-${field.key}`}>
          <option value="">— Pilih —</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === "pembimbing_select" ? (
        <select
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
          data-testid={`modal-${field.key}`}>
          <option value="">— Belum ditentukan —</option>
          {pembimbingNames.map((n) => <option key={n} value={n}>{n}</option>)}
          {/* Include current value even if not in list (e.g. renamed pembimbing) */}
          {value && !pembimbingNames.includes(value) && (
            <option value={value}>{value} (tidak aktif)</option>
          )}
        </select>
      ) : field.type === "textarea" ? (
        <textarea
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
          data-testid={`modal-${field.key}`}
        />
      ) : field.type === "date" ? (
        <input
          type={/^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) || !value ? "date" : "text"}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="YYYY-MM-DD atau bebas"
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
          data-testid={`modal-${field.key}`}
        />
      ) : field.type === "year" ? (
        <input
          type="number" min="2000" max="2099" step="1"
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="mis. 2023"
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
          data-testid={`modal-${field.key}`}
        />
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            if (!e.target.value) {
              const initial = field.type === "phone" ? "+62 " : field.type === "currency" ? "Rp. " : "";
              if (initial) onChange(initial);
            }
          }}
          disabled={readOnly}
          placeholder={field.type === "phone" ? "+62 …" : field.type === "currency" ? "Rp. …" : ""}
          className={`w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm ${readOnly ? "opacity-70" : ""}`}
          autoComplete="off"
          data-testid={`modal-${field.key}`}
        />
      )}
      {passwordHelp && <p className="mt-1 text-[10px] text-moss">{passwordHelp}</p>}
    </div>
  );
}

function ConfirmDialog({ record, onCancel, onConfirm, busy }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-forest/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="bg-parchment rounded-2xl w-full max-w-md p-6 border border-gold/40 shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="confirm-delete">
        <h3 className="font-serif-display text-forest text-2xl">Hapus data santri?</h3>
        <p className="mt-3 text-sm text-moss">
          Akun <b>{record.username}</b>{record.nama_santri ? ` (${record.nama_santri})` : ""} akan dihapus permanen. Aksi ini tidak bisa dibatalkan.
        </p>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onCancel} className="px-5 py-2 rounded-full text-forest text-sm hover:bg-sand transition-colors" data-testid="confirm-cancel">Batal</button>
          <button onClick={onConfirm} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-red-600 text-white text-sm hover:bg-red-700 transition-colors disabled:opacity-60" data-testid="confirm-ok">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Hapus
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ImportResultDialog({ result, onClose }) {
  const { created = 0, updated = 0, skipped = 0, errors = [] } = result || {};
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[75] bg-forest/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="bg-parchment rounded-2xl w-full max-w-lg p-6 md:p-8 border border-gold/40 shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="import-result">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Ringkasan Import</span>
            <h3 className="mt-1 font-serif-display text-forest text-2xl">Hasil impor data santri</h3>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2" aria-label="Tutup"><X size={18} /></button>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
            <div className="font-serif-display text-3xl text-emerald-700">{created}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-800 mt-1">Baru</div>
          </div>
          <div className="p-4 rounded-xl bg-sand border border-gold/30 text-center">
            <div className="font-serif-display text-3xl text-forest">{updated}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-moss mt-1">Diperbarui</div>
          </div>
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-center">
            <div className="font-serif-display text-3xl text-red-700">{skipped}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-red-800 mt-1">Dilewati</div>
          </div>
        </div>
        {errors.length > 0 && (
          <div className="mt-5 p-4 rounded-xl border border-red-200 bg-red-50">
            <p className="text-xs tracking-[0.2em] uppercase text-red-700 mb-2">Catatan ({errors.length})</p>
            <ul className="space-y-1 text-xs text-red-800 max-h-40 overflow-y-auto">
              {errors.map((e, i) => (<li key={i} className="font-mono">• {e}</li>))}
            </ul>
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors" data-testid="import-result-close">Selesai</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ImageUploader({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png)$/i.test(file.type)) {
      toast.error("Format harus JPG, JPEG, atau PNG");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran maksimal 5MB");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeImage(file, 512);
      onChange(dataUrl);
    } catch {
      toast.error("Gagal memproses gambar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-start gap-4">
      <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-gold/40 bg-sand flex-shrink-0 flex items-center justify-center">
        {value ? (
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-moss">Belum ada</span>
        )}
      </div>
      <div className="flex-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={testId}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors disabled:opacity-60"
            data-testid={`${testId}-pick`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
            {value ? "Ganti Foto" : "Pilih Foto"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-red-700 hover:text-red-800 px-3 py-2"
              data-testid={`${testId}-clear`}
            >
              Hapus
            </button>
          )}
        </div>
        <p className="mt-2 text-[10px] text-moss">
          Format JPG/JPEG/PNG · Otomatis dikompres ke 512px · Maks 5MB
        </p>
      </div>
    </div>
  );
}

function resizeImage(file, maxDim) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // JPEG for smaller output, 0.85 quality
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
